// @ts-check
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton,
  Box,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import COLORS from '@/resources/style/Themes';
import { useGroupLocationConfiguration } from '@/hooks/useGroupLocationConfiguration';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { StringKeys } from '@/resources/strings/StringKeys';
import { t } from 'i18next';
import Group from '@/models/Organization/Group';

/**
 * @typedef {object} GroupConfigurationModalProps
 * @property {boolean} open
 * @property {() => void} onClose
 * @property {Group} group
 */

/**
 *
 * @param {GroupConfigurationModalProps} props
 * @returns
 */
export default function GroupConfigurationModal({ open, onClose, group }) {
  const initialLocationSetting = useMemo(
    () => (group?.allow_group_location_tracking ? 'allow' : 'emergency'),
    [group],
  );
  const [locationSharingSetting, setLocationSharingSetting] = useState(
    initialLocationSetting,
  );
  const { saving, updateGroupLocationTracking } =
    useGroupLocationConfiguration();
  const { updateGroupLocationSetting } = useOrganizationUpdaterProvider();

  useEffect(() => {
    if (open) setLocationSharingSetting(initialLocationSetting);
  }, [open, initialLocationSetting]);

  const titleId = 'group-config-title';
  const groupName = (group?.group_name || t(StringKeys.LOADING)).toUpperCase();
  const titleText = t(StringKeys.CONFIGURE_GROUP, { groupName });

  const handleSave = async () => {
    const allowLocationTracking = locationSharingSetting === 'allow';

    const result = await updateGroupLocationTracking(
      group,
      allowLocationTracking,
    );

    if (result?.success) {
      // Update the groups state in OrganizationProvider
      updateGroupLocationSetting(group.group_name, allowLocationTracking);
      onClose();
    } else {
      setLocationSharingSetting(initialLocationSetting);
    }
  };

  const handleCancel = () => {
    setLocationSharingSetting(initialLocationSetting);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth={false}
      keepMounted
      slotProps={{
        paper: {
          sx: {
            width: 'fit-content',
            minWidth: { xs: 'auto', sm: 500 },
            maxWidth: '90vw',
            borderRadius: 1,
            overflow: 'hidden',
          },
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >
      <Box
        component="header"
        id={titleId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: COLORS.SAFEGUARD_BLUE,
          color: COLORS.WHITE,
          m: 0,
          py: 1.5,
          px: 1.5,
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            flex: 1,
            minWidth: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: { xs: '1rem', sm: '1.25rem' },
          }}
        >
          {titleText}
        </Typography>

        <IconButton
          onClick={saving ? undefined : onClose}
          size="small"
          aria-label="Close dialog"
          sx={{
            color: COLORS.WHITE,
            flexShrink: 0,
            '&:hover': { backgroundColor: COLORS.GREY },
          }}
          disabled={saving}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2 }}>
        <FormControl component="fieldset" fullWidth>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 'bold' }}>
            {t(StringKeys.LOCATION_SHARING)}
          </Typography>

          <RadioGroup
            value={locationSharingSetting}
            onChange={(e) => setLocationSharingSetting(e.target.value)}
            name="location-sharing"
          >
            <FormControlLabel
              value="allow"
              control={<Radio size="small" />}
              sx={{
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  display: 'block',
                },
              }}
              label={
                <Box component="span">
                  <Typography variant="body1" mt={1}>
                    {t(StringKeys.ALLOW_LOCATION_SHARING)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.GREY, mt: 0.25 }}
                  >
                    {t(StringKeys.WORKERS_CAN_CHOOSE_TO_SHARE_LOCATION)}:
                  </Typography>
                  <Box sx={{ mt: 0.25, mb: 0, pl: 2.5, color: COLORS.GREY }}>
                    <Typography variant="body2">
                      - {t(StringKeys.WHENEVER_COMPASS_IS_CONNECTED)}
                    </Typography>
                    <Typography variant="body2">
                      - {t(StringKeys.ONLY_DURING_EMERGENCIES)}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.GREY, mt: 0.25 }}
                  >
                    ({t(StringKeys.THEY_CANNOT_TURN_IT_OFF_ENTIRELY)})
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              value="emergency"
              control={<Radio size="small" />}
              sx={{
                alignItems: 'flex-start',
                mt: 1,
                '& .MuiFormControlLabel-label': {
                  display: 'block',
                },
              }}
              label={
                <Box component="span">
                  <Typography variant="body1" mt={1}>
                    {t(StringKeys.EMERGENCY_ONLY)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: COLORS.GREY, mt: 0.25 }}
                  >
                    {t(
                      StringKeys.LOCATION_IS_SHARED_ONLY_DURING_EMERGENCY_EVENTS,
                    )}
                  </Typography>
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          px: 1,
          py: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            borderRadius: 2,
            minWidth: 80,
            color: COLORS.GREY,
            borderColor: COLORS.GREY,
          }}
          disabled={saving}
        >
          {t(StringKeys.CANCEL)}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          sx={{
            minWidth: 80,
          }}
          disabled={saving}
        >
          {saving ? t(StringKeys.SAVING) : t(StringKeys.SAVE)}
        </Button>
      </Box>
    </Dialog>
  );
}
